import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Chip, Button, LinearProgress,
  CircularProgress, Tooltip as MuiTooltip, Skeleton,
} from '@mui/material';
import {
  BarChart3, TrendingUp, Users, Briefcase, CheckCircle2, Lock,
  Star, ArrowRight, Crown, Zap, FileText, Filter, Download,
  Target, Award, Info, Brain, Shield, Lightbulb, RefreshCw,
} from 'lucide-react';
import { motion } from 'framer-motion';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
  ResponsiveContainer, Cell, BarChart, Bar, Legend,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import DonutChart from '../components/analytics/DonutChart';

const MotionCard = motion(Card);

/* ── Status sets (shared) ──────────────────────────────────────────────────── */
const SHORTLISTED_PLUS = new Set(['application_shortlisted']);
const INTERVIEWED_PLUS = new Set([
  'ai_coding_scheduled', 'technical_scheduled', 'hr_scheduled',
  'ai_coding_completed', 'technical_completed', 'hr_completed',
  'technical_interview_completed', 'hr_interview_completed',
  'ai_technical_interview_scheduled', 'ai_technical_interview_completed',
  'technical_l2_interview_scheduled', 'technical_l2_interview_completed',
  'ai_hr_interview_scheduled', 'ai_hr_interview_completed',
  'hr_l2_interview_scheduled', 'hr_l2_interview_completed',
]);
const OFFERED_PLUS = new Set(['offer_released', 'candidate_selected']);

/* ── Data helpers ──────────────────────────────────────────────────────────── */
function computeTrend(appList) {
  const now = new Date();
  const months = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
    return {
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleDateString('en-US', { month: 'short' }),
      applications: 0,
    };
  });
  appList.forEach(a => {
    if (!a.created_at) return;
    const d = new Date(a.created_at);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const slot = months.find(m => m.key === key);
    if (slot) slot.applications++;
  });
  return months.map(({ label, applications }) => ({ month: label, applications }));
}

function computeProfTrend(appList) {
  const now = new Date();
  const months = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
    return {
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleDateString('en-US', { month: 'short' }),
      applications: 0,
      shortlisted: 0,
    };
  });
  appList.forEach(a => {
    if (!a.created_at) return;
    const d = new Date(a.created_at);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const slot = months.find(m => m.key === key);
    if (slot) {
      slot.applications++;
      if (SHORTLISTED_PLUS.has(a.status)) slot.shortlisted++;
    }
  });
  return months.map(({ label, applications, shortlisted }) => ({ month: label, applications, shortlisted }));
}

function computeStatusData(appList) {
  if (!appList.length) return [];
  const total = appList.length;
  let pending = 0, shortlisted = 0, interviewed = 0, offered = 0, rejected = 0;
  appList.forEach(a => {
    const s = a.status || '';
    if (s === 'pending') {
      pending++;
    } else if (s === 'application_shortlisted') {
      shortlisted++;
    } else if (INTERVIEWED_PLUS.has(s)) {
      interviewed++;
    } else if (['offer_released', 'candidate_selected'].includes(s)) {
      offered++;
    } else if (['candidate_rejected', 'terminated', 'technical_cancelled'].includes(s)) {
      rejected++;
    } else {
      // Fallback for any other statuses to ensure consistent counts
      pending++;
    }
  });

  const pct = (val) => total > 0 ? Math.round((val / total) * 100) : 0;

  return [
    { label: 'Pending Review', value: pending, percentage: pct(pending), color: '#f59e0b' },
    { label: 'Shortlisted', value: shortlisted, percentage: pct(shortlisted), color: '#1e3a8a' },
    { label: 'Interviewed', value: interviewed, percentage: pct(interviewed), color: '#1E3A8A' },
    { label: 'Offered', value: offered, percentage: pct(offered), color: '#0ea5e9' },
    { label: 'Rejected', value: rejected, percentage: pct(rejected), color: '#ef4444' },
  ].filter(d => d.value > 0);
}

function computeAiDistribution(appList) {
  const buckets = [
    { range: '0–20', count: 0, color: '#ef4444' },
    { range: '21–40', count: 0, color: '#f97316' },
    { range: '41–60', count: 0, color: '#f59e0b' },
    { range: '61–80', count: 0, color: '#1e3a8a' },
    { range: '81–100', count: 0, color: '#1E3A8A' },
  ];
  let hasAny = false;
  appList.forEach(a => {
    const s = Number(a.ai_score || 0);
    if (!s) return;
    hasAny = true;
    if (s <= 20) buckets[0].count++;
    else if (s <= 40) buckets[1].count++;
    else if (s <= 60) buckets[2].count++;
    else if (s <= 80) buckets[3].count++;
    else buckets[4].count++;
  });
  return buckets;
}

function computeFunnel(appList) {
  const total = appList.length;
  if (!total) return [];
  const s = Math.min(appList.filter(a => SHORTLISTED_PLUS.has(a.status)).length, total);
  const i = Math.min(appList.filter(a => INTERVIEWED_PLUS.has(a.status)).length, s);
  const o = Math.min(appList.filter(a => OFFERED_PLUS.has(a.status)).length, i);
  return [
    { label: 'Total Applications', value: total,  pct: 100,                                    color: '#1e3a8a', tip: 'All candidates who submitted an application to any of your job postings.' },
    { label: 'Shortlisted',        value: s,       pct: Math.round(s / total * 100),             color: '#3b82f6', tip: 'Candidates advanced past initial review — status is shortlisted or any later stage.' },
    { label: 'Interviews',         value: i,       pct: Math.round(i / total * 100),             color: '#1E3A8A', tip: 'Candidates who completed at least one interview round (AI, technical, or HR).' },
    { label: 'Offers Extended',    value: o,       pct: Math.round(o / total * 100),             color: '#f59e0b', tip: 'Candidates who received a formal job offer.' },
  ];
}

/* ── InfoTooltip ───────────────────────────────────────────────────────────── */
function InfoTooltip({ text }) {
  return (
    <MuiTooltip
      title={
        <Typography
          sx={{
            color: '#FFFFFF !important',
            fontSize: '0.82rem',
            fontWeight: 500,
            lineHeight: 1.6,
            maxWidth: 320,
            p: 0.5,
            opacity: 1
          }}
        >
          {text}
        </Typography>
      }
      arrow
      placement="top"
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: '#1e293b',
            color: '#FFFFFF !important',
            borderRadius: '10px',
            py: 1.5,
            px: 2,
            maxWidth: 320,
            '& *': {
              color: '#FFFFFF !important'
            }
          }
        },
        arrow: { sx: { color: '#1e293b' } },
      }}
    >
      <Box component="span" sx={{
        display: 'inline-flex', cursor: 'help', color: '#cbd5e1', ml: 0.5,
        verticalAlign: 'middle', '&:hover': { color: '#1e3a8a' }, transition: 'color 0.15s',
      }}>
        <Info size={13} />
      </Box>
    </MuiTooltip>
  );
}

/* ── SectionHeader ─────────────────────────────────────────────────────────── */
function SectionHeader({ icon, title, badge, description, tooltip }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: description ? 0.5 : 0 }}>
        {icon && <Box sx={{ color: '#1e3a8a', display: 'flex' }}>{icon}</Box>}
        <Typography sx={{ fontWeight: 700, color: '#111827', fontSize: '0.95rem' }}>{title}</Typography>
        {badge && (
          <Chip label={badge} size="small" sx={{ bgcolor: '#EFF6FF', color: '#1e3a8a', fontWeight: 700, fontSize: '0.6rem' }} />
        )}
        {tooltip && <InfoTooltip text={tooltip} />}
      </Box>
      {description && (
        <Typography sx={{ fontSize: '0.76rem', color: '#6b7280', lineHeight: 1.55 }}>{description}</Typography>
      )}
    </Box>
  );
}

/* ── ChartEmptyState ───────────────────────────────────────────────────────── */
function ChartEmptyState({ icon, message, subtext }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 5, gap: 1.5 }}>
      <Box sx={{
        width: 48, height: 48, borderRadius: '14px', bgcolor: '#f8fafc',
        display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#d1d5db',
      }}>
        {icon || <BarChart3 size={22} />}
      </Box>
      <Box sx={{ textAlign: 'center' }}>
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#9ca3af', mb: 0.3 }}>{message}</Typography>
        {subtext && (
          <Typography sx={{ fontSize: '0.75rem', color: '#c4c9d4', lineHeight: 1.5, maxWidth: 260, mx: 'auto' }}>
            {subtext}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

/* ── StatCard ──────────────────────────────────────────────────────────────── */
function StatCard({ icon, label, value, delay = 0, tooltip }) {
  const color = '#1E3A8A';
  return (
    <MotionCard
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay }}
      elevation={0}
      sx={{
        borderRadius: '16px', border: '1px solid #f1f5f9',
        borderBottom: `4px solid ${color}`,
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        height: '100%',
        '&:hover': { 
          boxShadow: '0 8px 24px rgba(30,58,138,0.12)', 
          transform: 'translateY(-4px)' 
        },
        transition: 'all 0.3s ease',
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
          <Box sx={{
            width: 42, height: 42, borderRadius: '12px',
            background: `rgba(30,58,138,0.10)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', color,
          }}>
            {icon}
          </Box>
          {tooltip && <InfoTooltip text={tooltip} />}
        </Box>
        <Typography sx={{ fontSize: '1.75rem', fontWeight: 800, color: (value === '—' || value == null) ? '#c4c9d4' : '#111827', lineHeight: 1 }}>
          {value ?? '—'}
        </Typography>
        <Typography sx={{ fontSize: '0.8rem', color: '#374151', fontWeight: 600, mt: 0.5 }}>{label}</Typography>
      </CardContent>
    </MotionCard>
  );
}

/* ── LockedFeatureCard ─────────────────────────────────────────────────────── */
function LockedFeatureCard({ icon, title, description, color, onUpgrade }) {
  return (
    <MotionCard
      initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }}
      elevation={0}
      sx={{ borderRadius: '16px', border: '1.5px dashed #e5e7eb', background: '#fafafa', opacity: 0.85 }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1 }}>
          <Box sx={{
            width: 38, height: 38, borderRadius: '10px', background: `${color}18`,
            color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            {icon}
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151' }}>{title}</Typography>
              <Lock size={13} color="#9ca3af" />
            </Box>
            <Typography sx={{ fontSize: '0.78rem', color: '#9ca3af' }}>{description}</Typography>
          </Box>
        </Box>
        <Button
          size="small" variant="outlined" onClick={onUpgrade}
          sx={{
            mt: 1, borderRadius: '8px', fontSize: '0.72rem', fontWeight: 600,
            borderColor: '#3b82f6', color: '#3b82f6', textTransform: 'none',
            '&:hover': { bgcolor: '#EFF6FF', borderColor: '#1e3a8a' },
          }}
          startIcon={<Crown size={12} />}
        >
          Upgrade to Professional
        </Button>
      </CardContent>
    </MotionCard>
  );
}

/* ── LOCKED_FEATURES constant ──────────────────────────────────────────────── */
const LOCKED_FEATURES = [
  { icon: <Users size={22} />,    title: 'Candidate Comparison',  description: 'Compare up to 5 candidates side-by-side with AI scoring.', color: '#1e3a8a' },
  { icon: <BarChart3 size={22} />, title: 'Recruiter Analytics',   description: 'Detailed recruiter performance metrics and team insights.',  color: '#3b82f6' },
  { icon: <Download size={22} />, title: 'Export Reports',         description: 'Export analytics data as PDF, CSV, or Excel.',               color: '#ec4899' },
  { icon: <Users size={22} />,    title: 'Team Performance',       description: 'Track and compare hiring team efficiency over time.',         color: '#f59e0b' },
  { icon: <Filter size={22} />,   title: 'Advanced Filters',       description: 'Filter analytics by date, role, team, and source.',          color: '#1E3A8A' },
];

const PRO_FEATURES = [
  { icon: <Users size={22} />,    title: 'Candidate Comparison',         description: 'AI-powered side-by-side comparison of top candidates.',          color: '#1e3a8a', route: '/analytics/candidate-comparison' },
  { icon: <BarChart3 size={22} />, title: 'Advanced AI Score Breakdown', description: 'Deep dive into every dimension of AI scoring.',                   color: '#3b82f6' },
  { icon: <FileText size={22} />, title: 'Interview & Answer Analysis',   description: 'Full transcript analysis with AI insights per candidate.',        color: '#ec4899' },
  { icon: <Target size={22} />,   title: 'Trust & Integrity Analytics',  description: 'Behavioral signals, risk scores and anomaly detection.',          color: '#f59e0b' },
  { icon: <Download size={22} />, title: 'Export Reports',                description: 'Export comparison reports as PDF or CSV.',                        color: '#1E3A8A' },
];

/* ─────────────────────────── STARTER ANALYTICS ─────────────────────────── */
function StarterAnalytics() {
  const navigate  = useNavigate();
  const [stats, setStats]     = useState(null);
  const [appList, setAppList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dashRes, aiRes, trendRes] = await Promise.allSettled([
          api.get('/admin/jobs/analytics/dashboard'),
          api.get('/admin/jobs/analytics/ai-distribution'),
          api.get('/admin/jobs/analytics/trend?range=6m'),
        ]);

        const dashData = dashRes.status === 'fulfilled' ? dashRes.value.data : {};
        const aiData   = aiRes.status   === 'fulfilled' ? aiRes.value.data   : {};
        const trend    = trendRes.status === 'fulfilled' ? trendRes.value.data  : {};

        setStats({
          activeJobs:          dashData.activeJobs || 0,
          totalApplications:    dashData.totalApplications || 0,
          shortlisted:         dashData.shortlisted || 0,
          avgAiScore:          dashData.avgAiScore,
          completedInterviews: dashData.completedAiInterviews || 0,
          scheduledInterviews: dashData.totalAiInterviews || 0,
          interviewCompletion: dashData.aiCompletionRate,
          hiringConversion:    dashData.hiringConversion,
          offerCount:          dashData.offers || 0,
          funnel:              dashData.funnel || [],
          pipeline:            dashData.pipeline || [],
          aiDistribution:      aiData.distribution || [],
          trend:               trend.trend || [],
        });
      } catch (err) {
        console.error('Failed to fetch starter analytics:', err);
        setStats({ activeJobs: 0, totalApplications: 0, shortlisted: 0, avgAiScore: null, completedInterviews: 0, scheduledInterviews: 0, interviewCompletion: null, hiringConversion: null, offerCount: 0, funnel: [], pipeline: [], aiDistribution: [], trend: [] });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const trendData  = stats?.trend || [];
  const statusData = stats?.pipeline || [];
  const aiDistData = stats?.aiDistribution || [];
  const funnelData = stats?.funnel || [];

  const hasTrend  = trendData.some(d => d.applications > 0);
  const hasFunnel = funnelData.length > 0 && funnelData[0]?.value > 0;
  const hasAiDist = aiDistData.length > 0 && aiDistData.some(d => d.count > 0);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const shortlistRate = stats?.totalApplications > 0
    ? Math.round((stats.shortlisted / stats.totalApplications) * 100)
    : 0;

  const statCards = [
    {
      icon: <Briefcase size={20} />, label: 'Active Jobs', value: stats?.activeJobs ?? 0, delay: 0,
      tooltip: 'Number of your job postings currently set to "active" and accepting new applications. Pulled directly from your jobs table. Helps you track how many open positions you are actively hiring for right now.',
    },
    {
      icon: <Users size={20} />, label: 'Total Applications', value: stats?.totalApplications ?? 0, delay: 0.06,
      tooltip: 'Total number of candidate applications received across all your job postings, regardless of their current status. Every time a candidate submits an application, this count increases. Measures job visibility and hiring demand.',
    },
    {
      icon: <CheckCircle2 size={20} />, label: 'Shortlisted', value: stats?.shortlisted ?? 0, delay: 0.12,
      tooltip: 'Candidates who passed your initial review and were moved forward. Counts applications currently at "shortlisted", "interview_scheduled", or any subsequent stage. A shortlist rate above 20–30% suggests well-written job descriptions.',
    },
    {
      icon: <Star size={20} />, label: 'Avg AI Score', value: stats?.avgAiScore != null ? `${stats.avgAiScore}/100` : '—', delay: 0.18,
      tooltip: 'Average AI interview performance score — calculated only from candidates who completed an AI interview (score > 0). Formula: Sum of all AI scores ÷ Number of completed AI interviews. Scores above 70 indicate a strong candidate pool.',
    },
    {
      icon: <TrendingUp size={20} />, label: 'Interview Completion', value: stats?.interviewCompletion != null ? `${stats.interviewCompletion}%` : '—', delay: 0.24,
      tooltip: 'Percentage of scheduled interviews that candidates actually completed. Formula: Completed Interviews ÷ Scheduled Interviews × 100. A low rate (below 60%) often indicates scheduling friction or poor candidate engagement before the interview.',
    },
    {
      icon: <Award size={20} />, label: 'Hiring Conversion', value: stats?.hiringConversion != null ? `${stats.hiringConversion}%` : '—', delay: 0.3,
      tooltip: 'Percentage of total applicants who received a job offer. Formula: (Offers Extended ÷ Total Applications) × 100. Industry benchmark is 1–3%. A higher rate means your screening and interviews are efficiently identifying the right candidates.',
    },
  ];

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 2, md: 3 }, py: 3, minHeight: '100vh' }}>

      {/* ── Header ── */}
      <motion.div initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
              <Typography variant="h4" sx={{ color: "#1E3A8A", fontWeight: "bold", fontSize: { xs: '1.5rem', md: '2rem' } }}>
                        Analytics Dashboard
                      </Typography>
              <Chip label="STARTER" size="small" sx={{ bgcolor: '#f0fdf4', color: '#16a34a', fontWeight: 700, fontSize: '0.65rem' }} />
            </Box>
            <Typography sx={{ color: '#6b7280', fontSize: '0.875rem' }}>
              Real-time hiring performance metrics — calculated from your live recruiter data.
            </Typography>
          </Box>
          <Button
            variant="contained" startIcon={<Crown size={16} />}
            onClick={() => navigate('/pricing?plan=professional')}
            sx={{
              borderRadius: '10px', textTransform: 'none', fontWeight: 700,
              background: 'linear-gradient(135deg, #1e3a8a, #3b82f6)',
              boxShadow: '0 4px 12px rgba(30,58,138,0.3)',
              '&:hover': { background: 'linear-gradient(135deg, #1e3a8a, #1e3a8a)', boxShadow: '0 6px 16px rgba(30,58,138,0.4)' },
            }}
          >
            Upgrade to Pro
          </Button>
        </Box>
      </motion.div>

      {/* ── KPI Section Header ── */}
      <SectionHeader
        icon={<BarChart3 size={16} />}
        title="Key Performance Indicators"
        description="Six real-time metrics calculated from your application and interview data. Hover the info icon on any card to see exactly how that number is computed."
        tooltip="These KPIs update every time you load the page. All values are derived from your actual recruiter data — no estimates or demo figures."
      />

      {/* ── KPI Cards ── */}
      <Grid container spacing={2} sx={{ mb: 3.5 }}>
        {statCards.map((card, i) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={i}>
            <StatCard {...card} />
          </Grid>
        ))}
      </Grid>

      {/* ── Application Trend + Pipeline ── */}
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>

        {/* Application Trend */}
        <Grid item xs={12} md={8}>
          <MotionCard
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.3 }}
            elevation={0}
            sx={{ borderRadius: '16px', border: '1px solid #f1f5f9', background: '#fff', height: '100%' }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <SectionHeader
                icon={<TrendingUp size={16} />}
                title="Application Trend"
                description="Monthly application volume over the last 6 months. Spikes show campaign effectiveness or seasonal demand. Flat months may need a sourcing push."
                tooltip="Applications are grouped by the month their created_at timestamp falls in. Covers the last 6 rolling months. Zero for a month means no applications were submitted that month — not missing data."
              />
              {!hasTrend ? (
                <ChartEmptyState
                  icon={<TrendingUp size={22} />}
                  message="No application trend data yet."
                  subtext="Monthly application volume will appear here once candidates start applying to your jobs."
                />
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={trendData}>
                    <defs>
                      <linearGradient id="appGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#1e3a8a" stopOpacity={0.22} />
                        <stop offset="95%" stopColor="#1e3a8a" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f4f8" />
                    <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 12, fill: '#9ca3af' }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <ReTooltip
                      contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.1)', fontSize: 12 }}
                      formatter={v => [`${v} application${v === 1 ? '' : 's'}`, 'Applications']}
                    />
                    <Area type="monotone" dataKey="applications" stroke="#1e3a8a" strokeWidth={2.5} fill="url(#appGrad)" dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </MotionCard>
        </Grid>

        {/* Candidate Pipeline */}
        <Grid item xs={12} md={4}>
          <MotionCard
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.35 }}
            elevation={0}
            sx={{ borderRadius: '16px', border: '1px solid #f1f5f9', background: '#fff', height: '100%' }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <SectionHeader
                icon={<Users size={16} />}
                title="Candidate Pipeline"
                description="Live distribution of where candidates currently sit in your hiring process. Hover a slice to see its percentage."
                tooltip="Each slice represents the percentage of all your applications currently at that pipeline stage. A large 'Pending Review' slice means you have a backlog of unreviewed applicants. Calculated from the live status field on each application record."
              />
              {statusData.length === 0 ? (
                <ChartEmptyState
                  icon={<Users size={22} />}
                  message="No pipeline data yet."
                  subtext="Status distribution appears once candidates start applying and you move them through stages."
                />
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                  <DonutChart data={statusData} size={140} label="Pipeline" />
                </Box>
              )}
            </CardContent>
          </MotionCard>
        </Grid>
      </Grid>

      {/* ── AI Score Distribution + Hiring Funnel ── */}
      <Grid container spacing={2.5} sx={{ mb: 4 }}>

        {/* AI Score Distribution */}
        <Grid item xs={12} md={6}>
          <MotionCard
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.4 }}
            elevation={0}
            sx={{ borderRadius: '16px', border: '1px solid #f1f5f9', background: '#fff' }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <SectionHeader
                icon={<Brain size={16} />}
                title="AI Score Distribution"
                description="Candidates grouped into performance bands by AI interview score (0–100). Only candidates who completed an AI interview are counted."
                tooltip="Scores come directly from the ai_score field on each application. Candidates with no AI score (score = 0 or null) are excluded. An ideal talent pool has most candidates in the 61–80 and 81–100 bands. Red bars (0–40) indicate candidates who struggled with the AI interview questions."
              />
              {!hasAiDist ? (
                <ChartEmptyState
                  icon={<Brain size={22} />}
                  message="No AI interview scores yet."
                  subtext="Complete AI interviews with candidates to see how your talent pool performs across score bands."
                />
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={aiDistData} barSize={30}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f4f8" vertical={false} />
                      <XAxis dataKey="range" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <ReTooltip
                        contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.1)', fontSize: 12 }}
                        formatter={(v, _, p) => [`${v} candidate${v === 1 ? '' : 's'}`, `Score ${p.payload.range}`]}
                      />
                      <Bar dataKey="count" name="Candidates" radius={[6, 6, 0, 0]}>
                        {aiDistData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1.5, pt: 1.5, borderTop: '1px solid #f8fafc' }}>
                    {[
                      { label: '0–40', note: 'Below average', color: '#ef4444' },
                      { label: '41–60', note: 'Average', color: '#f59e0b' },
                      { label: '61–100', note: 'Strong performer', color: '#1E3A8A' },
                    ].map(b => (
                      <Box key={b.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: b.color, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.68rem', color: '#9ca3af' }}>{b.label} = {b.note}</Typography>
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </CardContent>
          </MotionCard>
        </Grid>

        {/* Hiring Funnel */}
        <Grid item xs={12} md={6}>
          <MotionCard
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.45 }}
            elevation={0}
            sx={{ borderRadius: '16px', border: '1px solid #f1f5f9', background: '#fff' }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <SectionHeader
                icon={<Target size={16} />}
                title="Hiring Funnel"
                description="Cumulative candidates at each stage — from application to hire. A sharp drop between rows reveals your pipeline's biggest bottleneck."
                tooltip="Each row shows how many candidates reached that stage or beyond (cumulative, not snapshot). The funnel is always monotonically decreasing: Applications ≥ Shortlisted ≥ Interviews ≥ Offers ≥ Hired — impossible values are automatically clamped. Hover each row's info icon for stage definitions."
              />
              {!hasFunnel ? (
                <ChartEmptyState
                  icon={<Target size={22} />}
                  message="No funnel data yet."
                  subtext="Your hiring funnel will populate as candidates apply and move through your pipeline stages."
                />
              ) : (
                funnelData.map((row, i) => (
                  <Box key={i} sx={{ mb: i < funnelData.length - 1 ? 2 : 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: '0.8rem', color: '#374151', fontWeight: 500 }}>{row.label}</Typography>
                        <InfoTooltip text={row.tip} />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography sx={{ fontSize: '0.73rem', color: '#9ca3af' }}>{row.pct}% of total</Typography>
                        <Typography sx={{ fontSize: '0.8rem', color: '#374151', fontWeight: 700, minWidth: 24, textAlign: 'right' }}>{row.value}</Typography>
                      </Box>
                    </Box>
                    <LinearProgress
                      variant="determinate" value={row.pct}
                      sx={{ height: 8, borderRadius: 4, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { bgcolor: row.color, borderRadius: 4 } }}
                    />
                  </Box>
                ))
              )}
            </CardContent>
          </MotionCard>
        </Grid>
      </Grid>

      {/* ── Locked Professional Features ── */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}>
        <SectionHeader
          icon={<Lock size={15} />}
          title="Professional Features"
          badge="PRO"
          description="These advanced analytics require a Professional plan. Upgrade to unlock AI-powered candidate comparison, team performance tracking, and report exports."
          tooltip="Professional features give you deeper hiring intelligence beyond what's available in Starter: compare candidates side-by-side using all interview scores, analyze recruiter team performance, and export any report as PDF or CSV. Available on the Annual (Professional) plan."
        />
        <Grid container spacing={2}>
          {LOCKED_FEATURES.map((f, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <LockedFeatureCard {...f} onUpgrade={() => navigate('/pricing?plan=professional')} />
            </Grid>
          ))}
        </Grid>
      </motion.div>
    </Box>
  );
}

/* ── generateInsights ──────────────────────────────────────────────────────── */
function generateInsights(dash, trend) {
  const out = [];
  if (!dash) return out;

  if (dash.shortlistRate != null) {
    if (dash.shortlistRate >= 30) {
      out.push({ level: 'success', title: 'Strong Shortlist Rate', body: `${dash.shortlistRate}% of applicants are shortlisted — above the 20–30% industry benchmark. Your job descriptions are attracting well-matched candidates.` });
    } else if (dash.shortlistRate < 10 && dash.totalApplications > 10) {
      out.push({ level: 'warning', title: 'Low Shortlist Rate', body: `Only ${dash.shortlistRate}% of applicants are shortlisted. Consider refining job descriptions or reviewing screening criteria to attract better-fit candidates.` });
    }
  }

  if (dash.aiCompletionRate != null) {
    if (dash.aiCompletionRate >= 80) {
      out.push({ level: 'success', title: 'Excellent AI Interview Adoption', body: `${dash.aiCompletionRate}% of AI interviews are completed — strong candidate engagement with your automated screening process.` });
    } else if (dash.aiCompletionRate < 60 && dash.totalAiInterviews > 5) {
      out.push({ level: 'warning', title: 'Low AI Interview Completion', body: `${dash.aiCompletionRate}% of AI interviews are completed. Below 60% often indicates friction in the interview flow or poor candidate engagement.` });
    }
  }

  if (dash.avgAiScore != null) {
    if (dash.avgAiScore >= 70) {
      out.push({ level: 'success', title: 'High-Scoring Talent Pool', body: `Average AI score of ${dash.avgAiScore}/100 — your pipeline has a strong concentration of high-performing candidates. Benchmark: 60+ is considered strong.` });
    } else if (dash.avgAiScore < 50 && dash.completedAiInterviews > 5) {
      out.push({ level: 'info', title: 'Moderate Candidate Scores', body: `Average AI score is ${dash.avgAiScore}/100. Most candidates score in the mid-range. Consider raising screening thresholds or sourcing from different channels.` });
    }
  }

  if (dash.hiringConversion != null && dash.totalApplications > 5) {
    if (dash.hiringConversion >= 5) {
      out.push({ level: 'success', title: 'Strong Hiring Conversion', body: `${dash.hiringConversion}% of applicants receive offers — above the 1–3% industry benchmark. Your pipeline is efficiently converting candidates to hires.` });
    } else if (dash.hiringConversion === 0 && dash.totalApplications > 20) {
      out.push({ level: 'info', title: 'No Offers Extended Yet', body: `You have ${dash.totalApplications} applications but no offers extended.` });
    }
  }

  if (trend && trend.length >= 2) {
    const recent = trend[trend.length - 1]?.applications || 0;
    const prev   = trend[trend.length - 2]?.applications || 0;
    if (prev > 0 && recent > prev * 1.3) {
      out.push({ level: 'success', title: 'Application Volume Surging', body: `${Math.round((recent - prev) / prev * 100)}% more applications this month vs last. Capitalize on this momentum by reviewing your pipeline backlog.` });
    } else if (prev > 0 && recent < prev * 0.5 && recent < prev - 3) {
      out.push({ level: 'warning', title: 'Declining Application Volume', body: `Application volume dropped ${Math.round((prev - recent) / prev * 100)}% this month. Consider refreshing job listings or boosting your job board presence.` });
    }
  }

  if (dash.avgTrustScore != null) {
    if (dash.avgTrustScore >= 80) {
      out.push({ level: 'success', title: 'High Candidate Integrity', body: `Average trust score of ${dash.avgTrustScore}/100 — candidates demonstrate strong behavioral authenticity in AI interviews with minimal deception signals.` });
    } else if (dash.avgTrustScore < 60) {
      out.push({ level: 'warning', title: 'Integrity Signals to Review', body: `Average trust score is ${dash.avgTrustScore}/100. Review individual AI interview reports for candidates with low trust scores before advancing them.` });
    }
  }

  return out.slice(0, 6);
}

/* ─────────────────────────── PROFESSIONAL ANALYTICS ─────────────────────── */
function ProfessionalAnalytics() {
  const navigate = useNavigate();
  const [dash, setDash]           = useState(null);
  const [trend, setTrend]         = useState([]);
  const [aiDist, setAiDist]       = useState([]);
  const [dateRange, setDateRange] = useState('6m');
  const [loading, setLoading]     = useState(true);
  const [trendLoading, setTrendLoading] = useState(false);
  const [error, setError]         = useState(null);

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [dashRes, aiRes] = await Promise.all([
        api.get('/admin/jobs/analytics/dashboard'),
        api.get('/admin/jobs/analytics/ai-distribution'),
      ]);
      setDash(dashRes.data);
      setAiDist(aiRes.data.distribution || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load analytics. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTrend = useCallback(async (range) => {
    try {
      setTrendLoading(true);
      const res = await api.get(`/admin/jobs/analytics/trend?range=${range}`);
      setTrend(res.data.trend || []);
    } catch {
      setTrend([]);
    } finally {
      setTrendLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    fetchTrend(dateRange);
  }, [fetchTrend, dateRange]);

  const insights = useMemo(() => generateInsights(dash, trend), [dash, trend]);

  const handleExportCSV = useCallback(() => {
    if (!dash) return;
    const rows = [
      ['Metric', 'Value'],
      ['Active Jobs', dash.activeJobs],
      ['Total Jobs', dash.totalJobs],
      ['Total Applications', dash.totalApplications],
      ['Shortlisted', dash.shortlisted],
      ['Shortlist Rate', `${dash.shortlistRate}%`],
      ['Offers Extended', dash.offers],
      ['Hired', dash.hired],
      ['Rejected', dash.rejected],
      ['Hiring Conversion', `${dash.hiringConversion}%`],
      ['Avg AI Score', dash.avgAiScore ?? 'N/A'],
      ['Avg Trust Score', dash.avgTrustScore ?? 'N/A'],
      ['AI Completion Rate', dash.aiCompletionRate != null ? `${dash.aiCompletionRate}%` : 'N/A'],
      ['Completed Coding Tests', dash.completedCodingTests],
      ['Completed Tech Interviews', dash.completedTechInterviews],
      ['Completed HR Interviews', dash.completedHrInterviews],
      [],
      ['Top Jobs by Applications'],
      ['Job Title', 'Total Applications', 'Shortlisted', 'Avg AI Score'],
      ...(dash.topJobs || []).map(j => [j.title, j.totalApplications, j.shortlisted, j.avgAiScore ?? 'N/A']),
    ];
    const csv  = rows.map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `exihire-analytics-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [dash]);

  if (loading) {
    return (
      <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 2, md: 3 }, py: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Skeleton variant="text" width={280} height={40} sx={{ mb: 1 }} />
          <Skeleton variant="text" width={400} height={24} />
        </Box>
        <Skeleton variant="rounded" height={140} sx={{ borderRadius: '20px', mb: 3 }} />
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[...Array(8)].map((_, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rounded" height={120} sx={{ borderRadius: '16px' }} />
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={2.5}>
          <Grid item xs={12} md={8}>
            <Skeleton variant="rounded" height={300} sx={{ borderRadius: '16px' }} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rounded" height={300} sx={{ borderRadius: '16px' }} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 560, mx: 'auto', px: 3, py: 8, textAlign: 'center' }}>
        <Box sx={{ width: 64, height: 64, borderRadius: '18px', mx: 'auto', mb: 3, bgcolor: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <RefreshCw size={28} color="#ef4444" />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', mb: 1 }}>Failed to Load Analytics</Typography>
        <Typography sx={{ color: '#6b7280', mb: 3, fontSize: '0.875rem' }}>{error}</Typography>
        <Button
          variant="contained" startIcon={<RefreshCw size={16} />} onClick={fetchDashboard}
          sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, background: 'linear-gradient(135deg, #1e3a8a, #3b82f6)' }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  const funnel  = dash?.funnel  || [];
  const topJobs = dash?.topJobs || [];
  const hasFunnel  = funnel.length > 0 && funnel[0]?.value > 0;
  const hasTrend   = trend.some(d => d.applications > 0);
  const hasAiDist  = aiDist.length > 0;
  const hasTopJobs = topJobs.length > 0;

  const INSIGHT_COLOR = { success: '#1E3A8A', warning: '#f59e0b', info: '#818cf8', error: '#f87171' };
  const INSIGHT_BORDER = { success: 'rgba(16,185,129,0.25)', warning: 'rgba(245,158,11,0.25)', info: 'rgba(30,58,138,0.25)', error: 'rgba(239,68,68,0.2)' };

  const kpiRow1 = [
    { icon: <Briefcase size={20} />, label: 'Active Jobs',         value: dash?.activeJobs ?? 0,           delay: 0,
      tooltip: 'Job postings currently set to "active" and accepting applications.' },
    { icon: <Users size={20} />,     label: 'Total Applications',  value: dash?.totalApplications ?? 0,    delay: 0.06,
      tooltip: 'Total candidate applications received across all job postings.' },
    { icon: <CheckCircle2 size={20} />, label: 'Shortlisted',      value: dash?.shortlisted ?? 0,          delay: 0.12,
      tooltip: 'Candidates advanced past initial review. Status = shortlisted or any later stage.' },
  ];

  const kpiRow2 = [
    { icon: <Star size={20} />,      label: 'Avg AI Score',        value: dash?.avgAiScore != null ? `${dash.avgAiScore}/100` : '—',       delay: 0.24,
      tooltip: 'Average AI interview score (score > 0 only). Formula: Sum of scores ÷ Completed AI interviews.' },
    { icon: <Shield size={20} />,    label: 'Avg Trust Score',     value: dash?.avgTrustScore != null ? `${dash.avgTrustScore}/100` : '—', delay: 0.3,
      tooltip: 'Average behavioral integrity score from AI interviews. High scores indicate authentic candidates with low deception signals.' },
    { icon: <TrendingUp size={20} />, label: 'AI Completion Rate', value: dash?.aiCompletionRate != null ? `${dash.aiCompletionRate}%` : '—', delay: 0.36,
      tooltip: 'Percentage of started AI interviews that were completed. Formula: Completed ÷ Total AI interviews × 100.' },
    { icon: <Target size={20} />,    label: 'Offers Extended',     value: dash?.offers ?? 0,               delay: 0.42,
      tooltip: 'Candidates who received a formal job offer. Status = offer_released.' },
  ];

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 2, md: 3 }, py: 3, minHeight: '100vh' }}>

      {/* ── Header ── */}
      <motion.div initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
              <Typography variant="h4" sx={{ color: "#1E3A8A", fontWeight: "bold", fontSize: { xs: '1.5rem', md: '2rem' } }}>
                        Analytics Dashboard
                      </Typography>
              <Chip label="PRO" size="small" icon={<Crown size={11} />}
                sx={{ bgcolor: '#EFF6FF', color: '#1e3a8a', fontWeight: 700, fontSize: '0.65rem', '& .MuiChip-icon': { color: '#1e3a8a' } }} />
            </Box>
            <Typography sx={{ color: '#6b7280', fontSize: '0.875rem' }}>
              Full professional analytics — all metrics from your live hiring database.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="outlined" startIcon={<Download size={15} />} onClick={handleExportCSV}
              sx={{
                borderRadius: '10px', textTransform: 'none', fontWeight: 700, fontSize: '0.82rem',
                borderColor: '#e5e7eb', color: '#374151',
                '&:hover': { borderColor: '#1e3a8a', color: '#1e3a8a', bgcolor: '#EFF6FF' },
              }}
            >
              Export CSV
            </Button>
            <Button
              variant="contained" startIcon={<Users size={16} />}
              onClick={() => navigate('/analytics/candidate-comparison')}
              sx={{
                borderRadius: '10px', textTransform: 'none', fontWeight: 700,
                background: 'linear-gradient(135deg, #1e3a8a, #3b82f6)',
                boxShadow: '0 4px 12px rgba(30,58,138,0.3)',
                '&:hover': { background: 'linear-gradient(135deg, #1e3a8a, #1e3a8a)', boxShadow: '0 6px 16px rgba(30,58,138,0.4)' },
              }}
            >
              Compare Candidates
            </Button>
          </Box>
        </Box>
      </motion.div>

      {/* ── AI Insights Panel ── */}
      {insights.length > 0 && (
        <MotionCard
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}
          elevation={0}
          sx={{
            borderRadius: '20px', mb: 3, overflow: 'hidden', position: 'relative',
            background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 60%, #1a1a2e 100%)',
            border: '1px solid rgba(30,58,138,0.2)',
          }}
        >
          <Box sx={{ position: 'absolute', top: 0, right: 0, width: 280, height: 280, background: 'radial-gradient(circle, rgba(30,58,138,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: '12px', background: 'linear-gradient(135deg, #1e3a8a, #3b82f6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Brain size={20} color="white" />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 800, color: '#fff', fontSize: '1rem' }}>AI Hiring Insights</Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem' }}>Computed from your live recruiter data</Typography>
              </Box>
            </Box>
            <Grid container spacing={2}>
              {insights.map((ins, i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Box sx={{ p: 2, borderRadius: '12px', background: 'rgba(255,255,255,0.04)', border: `1px solid ${INSIGHT_BORDER[ins.level] || INSIGHT_BORDER.info}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Box sx={{ mt: 0.2, flexShrink: 0 }}>
                        <Lightbulb size={14} color={INSIGHT_COLOR[ins.level] || INSIGHT_COLOR.info} />
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: INSIGHT_COLOR[ins.level] || INSIGHT_COLOR.info, mb: 0.3 }}>
                          {ins.title}
                        </Typography>
                        <Typography sx={{ fontSize: '0.73rem', color: '#94a3b8', lineHeight: 1.5 }}>
                          {ins.body}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </MotionCard>
      )}

      {/* ── KPI Row 1 ── */}
      <SectionHeader
        icon={<BarChart3 size={16} />}
        title="Key Performance Indicators"
        description="Eight real-time hiring metrics from your live database. Hover the info icon on any card for the exact calculation formula."
        tooltip="All KPIs recalculate on every page load from your live application, job, and interview data."
      />
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {kpiRow1.map((card, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}><StatCard {...card} /></Grid>
        ))}
      </Grid>
      <Grid container spacing={2} sx={{ mb: 3.5 }}>
        {kpiRow2.map((card, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}><StatCard {...card} /></Grid>
        ))}
      </Grid>

      {/* ── Application Trend + Hiring Funnel ── */}
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>

        <Grid item xs={12} md={8}>
          <MotionCard
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.3 }}
            elevation={0}
            sx={{ borderRadius: '16px', border: '1px solid #f1f5f9', background: '#fff', height: '100%' }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                <SectionHeader
                  icon={<TrendingUp size={16} />}
                  title="Applications vs Shortlisted"
                  description="Monthly comparison of applications received vs. candidates shortlisted."
                  tooltip="Purple = total applications submitted that month. Green = applications shortlisted or beyond. A narrowing gap means you're shortlisting a higher proportion of applicants."
                />
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, mt: 0.3 }}>
                  {['3m','6m','12m'].map(r => (
                    <Button
                      key={r} size="small" onClick={() => setDateRange(r)}
                      sx={{
                        minWidth: 0, px: 1.3, py: 0.4, borderRadius: '7px', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase',
                        bgcolor: dateRange === r ? '#1e3a8a' : 'transparent',
                        color:   dateRange === r ? '#fff'    : '#9ca3af',
                        '&:hover': { bgcolor: dateRange === r ? '#1e3a8a' : '#f1f5f9' },
                      }}
                    >
                      {r}
                    </Button>
                  ))}
                </Box>
              </Box>
              {trendLoading ? (
                <Skeleton variant="rounded" height={220} sx={{ borderRadius: '10px', mt: 1 }} />
              ) : !hasTrend ? (
                <ChartEmptyState icon={<TrendingUp size={22} />} message="No trend data available yet." subtext="Application and shortlist trends will appear here once candidates start applying." />
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={trend}>
                    <defs>
                      <linearGradient id="pAppGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#1e3a8a" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="#1e3a8a" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="pShortGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#1E3A8A" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="#1E3A8A" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f4f8" />
                    <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 12, fill: '#9ca3af' }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <ReTooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.1)', fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Area type="monotone" dataKey="applications" name="Applications" stroke="#1e3a8a" strokeWidth={2.5} fill="url(#pAppGrad)" dot={false} />
                    <Area type="monotone" dataKey="shortlisted"  name="Shortlisted"  stroke="#1E3A8A" strokeWidth={2.5} fill="url(#pShortGrad)" dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </MotionCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <MotionCard
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.35 }}
            elevation={0}
            sx={{ borderRadius: '16px', border: '1px solid #f1f5f9', background: '#fff', height: '100%' }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <SectionHeader
                icon={<Target size={16} />}
                title="Hiring Funnel"
                description="Cumulative candidate counts at each stage. A sharp drop reveals your biggest pipeline bottleneck."
                tooltip="Each stage is cumulative and always monotonically decreasing (Applications ≥ Shortlisted ≥ Interviews ≥ Offers). Values come directly from the database."
              />
              {!hasFunnel ? (
                <ChartEmptyState icon={<Target size={22} />} message="No funnel data yet." subtext="The hiring funnel will populate as candidates apply and progress." />
              ) : (
                funnel.map((row, i) => (
                  <Box key={i} sx={{ mb: i < funnel.length - 1 ? 1.8 : 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: '0.78rem', color: '#374151', fontWeight: 500 }}>{row.label}</Typography>
                        <InfoTooltip text={row.tip} />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af' }}>{row.pct}%</Typography>
                        <Typography sx={{ fontSize: '0.78rem', color: '#374151', fontWeight: 700, minWidth: 24, textAlign: 'right' }}>{row.value}</Typography>
                      </Box>
                    </Box>
                    <LinearProgress
                      variant="determinate" value={row.pct}
                      sx={{ height: 7, borderRadius: 4, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { bgcolor: row.color, borderRadius: 4 } }}
                    />
                  </Box>
                ))
              )}
            </CardContent>
          </MotionCard>
        </Grid>
      </Grid>

      {/* ── AI Score Distribution + Top Jobs ── */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>

        <Grid item xs={12} md={5}>
          <MotionCard
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.4 }}
            elevation={0}
            sx={{ borderRadius: '16px', border: '1px solid #f1f5f9', background: '#fff', height: '100%' }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <SectionHeader
                icon={<Brain size={16} />}
                title="AI Score Distribution"
                description="Candidates grouped into performance bands by AI interview score (0–100)."
                tooltip="Scores come from ai_interview_results.score. Only completed interviews (score > 0) are counted. An ideal distribution has most candidates in the 61–100 bands."
              />
              {!hasAiDist ? (
                <ChartEmptyState icon={<Brain size={22} />} message="No AI interview scores yet." subtext="Complete AI interviews with candidates to see performance band distribution." />
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={aiDist} barSize={30}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f4f8" vertical={false} />
                      <XAxis dataKey="range" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <ReTooltip
                        contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.1)', fontSize: 12 }}
                        formatter={(v, _, p) => [`${v} candidate${v === 1 ? '' : 's'}`, `Score ${p.payload.range}`]}
                      />
                      <Bar dataKey="count" name="Candidates" radius={[6, 6, 0, 0]}>
                        {aiDist.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 1.5, pt: 1.5, borderTop: '1px solid #f8fafc' }}>
                    {[{ label: '0–40', note: 'Below average', color: '#ef4444' }, { label: '41–60', note: 'Average', color: '#f59e0b' }, { label: '61–100', note: 'Strong', color: '#1E3A8A' }].map(b => (
                      <Box key={b.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: b.color }} />
                        <Typography sx={{ fontSize: '0.68rem', color: '#9ca3af' }}>{b.label} = {b.note}</Typography>
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </CardContent>
          </MotionCard>
        </Grid>

        <Grid item xs={12} md={7}>
          <MotionCard
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.45 }}
            elevation={0}
            sx={{ borderRadius: '16px', border: '1px solid #f1f5f9', background: '#fff', height: '100%' }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <SectionHeader
                icon={<Briefcase size={16} />}
                title="Top Jobs by Applications"
                description="Your 5 highest-volume job postings with shortlist counts and average AI scores."
                tooltip="Ranked by total application count. Avg AI Score averages only candidates with completed AI interviews. Shortlisted counts candidates at shortlisted or any later stage."
              />
              {!hasTopJobs ? (
                <ChartEmptyState icon={<Briefcase size={22} />} message="No job data yet." subtext="Post jobs and receive applications to see per-job performance here." />
              ) : (
                <Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px 72px', gap: 1, px: 1, py: 0.8, mb: 0.5, borderBottom: '1px solid #f1f5f9' }}>
                    {['Job Title','Applications','Shortlisted','AI Score'].map(h => (
                      <Typography key={h} sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</Typography>
                    ))}
                  </Box>
                  {topJobs.map((job, i) => {
                    const rowColors = ['#1e3a8a','#1E3A8A','#f59e0b','#ec4899','#0ea5e9'];
                    return (
                      <Box key={job.id} sx={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px 72px', gap: 1, px: 1, py: 1.2, borderRadius: '8px', alignItems: 'center', bgcolor: i % 2 === 0 ? 'transparent' : '#f9fafb', '&:hover': { bgcolor: '#f1f5f9' }, transition: 'background 0.15s' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                          <Box sx={{ width: 28, height: 28, borderRadius: '8px', bgcolor: `${rowColors[i % 5]}18`, color: rowColors[i % 5], display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '0.7rem', fontWeight: 800 }}>
                            {i + 1}
                          </Box>
                          <Typography sx={{ fontSize: '0.82rem', color: '#111827', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {job.title}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: '0.82rem', color: '#374151', fontWeight: 700, textAlign: 'center' }}>{job.totalApplications}</Typography>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography sx={{ fontSize: '0.82rem', color: '#374151', fontWeight: 700 }}>{job.shortlisted}</Typography>
                          {job.totalApplications > 0 && (
                            <Typography sx={{ fontSize: '0.65rem', color: '#9ca3af' }}>{Math.round(job.shortlisted / job.totalApplications * 100)}%</Typography>
                          )}
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                          {job.avgAiScore != null ? (
                            <Chip label={`${job.avgAiScore}`} size="small"
                              sx={{ bgcolor: job.avgAiScore >= 70 ? '#f0fdf4' : job.avgAiScore >= 50 ? '#fefce8' : '#fef2f2', color: job.avgAiScore >= 70 ? '#16a34a' : job.avgAiScore >= 50 ? '#ca8a04' : '#dc2626', fontWeight: 700, fontSize: '0.7rem', height: 20 }}
                            />
                          ) : (
                            <Typography sx={{ fontSize: '0.75rem', color: '#d1d5db' }}>—</Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </CardContent>
          </MotionCard>
        </Grid>
      </Grid>

      {/* ── Candidate Comparison CTA ── */}
      <MotionCard
        initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.5 }}
        elevation={0}
        sx={{ borderRadius: '20px', border: '1px solid #eff6ff', background: 'linear-gradient(135deg, #eef2ff 0%, #EFF6FF 100%)', mb: 3 }}
      >
        <CardContent sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 52, height: 52, borderRadius: '14px', background: 'linear-gradient(135deg, #1e3a8a, #3b82f6)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(30,58,138,0.3)' }}>
              <Users size={24} color="white" />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: '#1e1b4b' }}>Candidate Comparison</Typography>
              <Typography sx={{ fontSize: '0.82rem', color: '#1e3a8a', fontWeight: 500 }}>
                Compare up to 5 candidates side-by-side with AI scores, trust analysis, and hiring recommendations
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained" endIcon={<ArrowRight size={16} />}
            onClick={() => navigate('/analytics/candidate-comparison')}
            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, px: 3, background: 'linear-gradient(135deg, #1e3a8a, #3b82f6)', boxShadow: '0 4px 12px rgba(30,58,138,0.3)', '&:hover': { background: 'linear-gradient(135deg, #1e3a8a, #1e3a8a)' } }}
          >
            Open Comparison
          </Button>
        </CardContent>
      </MotionCard>

      {/* ── Pro Features Grid ── */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}>
        <SectionHeader
          title="Professional Features"
          description="All analytics capabilities included in your Professional plan."
          tooltip="These features are active. Click Candidate Comparison to start comparing candidates using AI scores and trust analysis."
        />
        <Grid container spacing={2}>
          {PRO_FEATURES.map((f, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <MotionCard
                initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: 0.65 + i * 0.05 }}
                elevation={0}
                onClick={f.route ? () => navigate(f.route) : undefined}
                sx={{ borderRadius: '16px', border: '1px solid #f1f5f9', background: '#fff', cursor: f.route ? 'pointer' : 'default', '&:hover': f.route ? { boxShadow: '0 6px 20px rgba(0,0,0,0.08)', transform: 'translateY(-2px)' } : {}, transition: 'all 0.2s' }}
              >
                <CardContent sx={{ p: 2.5, display: 'flex', gap: 1.5 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '10px', flexShrink: 0, background: `${f.color}18`, color: f.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {f.icon}
                  </Box>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#111827' }}>{f.title}</Typography>
                      {f.route && <ArrowRight size={13} color={f.color} />}
                    </Box>
                    <Typography sx={{ fontSize: '0.76rem', color: '#6b7280', mt: 0.3 }}>{f.description}</Typography>
                  </Box>
                </CardContent>
              </MotionCard>
            </Grid>
          ))}
        </Grid>
      </motion.div>
    </Box>
  );
}

/* ─────────────────────────── UPGRADE PROMPT ─────────────────────────────── */
function UpgradePrompt() {
  const navigate = useNavigate();
  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', px: 3, py: 8, textAlign: 'center' }}>
      <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}>
        <Box sx={{
          width: 72, height: 72, borderRadius: '20px', mx: 'auto', mb: 3,
          background: 'linear-gradient(135deg, #1e3a8a, #3b82f6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 8px 24px rgba(30,58,138,0.35)',
        }}>
          <BarChart3 size={36} color="white" />
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 800, color: '#111827', mb: 1 }}>
          Unlock Analytics Dashboard
        </Typography>
        <Typography sx={{ color: '#6b7280', mb: 3, lineHeight: 1.7 }}>
          Get AI-driven hiring analytics, candidate comparison tools, and detailed performance reports — available on Starter and Professional plans.
        </Typography>
        <Button
          variant="contained" size="large" startIcon={<Zap size={18} />}
          onClick={() => navigate('/pricing?plan=professional')}
          sx={{
            borderRadius: '12px', textTransform: 'none', fontWeight: 700, px: 4, py: 1.5,
            background: 'linear-gradient(135deg, #1e3a8a, #3b82f6)',
            boxShadow: '0 6px 16px rgba(30,58,138,0.4)',
            '&:hover': { background: 'linear-gradient(135deg, #1e3a8a, #1e3a8a)' },
          }}
        >
          View Plans & Upgrade
        </Button>
      </motion.div>
    </Box>
  );
}

/* ─────────────────────────── MAIN EXPORT ────────────────────────────────── */
export default function Analytics() {
  const { user } = useAuth();
  const isProfessional =
    user?.plan === 'professional' ||
    user?.plan === 'annual' ||
    user?.plan === 'custom';
  const isStarter      = user?.plan === 'monthly';

  if (isProfessional) return <ProfessionalAnalytics />;
  if (isStarter)      return <StarterAnalytics />;
  return <UpgradePrompt />;
}
